import moment, { Moment } from 'moment';
import { isCNLocale, logger } from '@marriott/shared/mi-helper-utils';
const { log } = logger({})('DateUtils');
export type DateObjectType = Moment;

export const DATE_FORMAT_YEAR_MONTH_DAY = 'YYYY-MM-DD';
const DATE_FORMAT_DEFAULT = 'ddd, MMM DD';
export const DATE_FORMAT_VALUE = 'MM/DD/YYYY';

export const getFormattedDate = (date: string, format = DATE_FORMAT_YEAR_MONTH_DAY): DateObjectType => {
  return moment(moment(date).format(format));
};

export const getDurationBetweenDates = (startDate: string, endDate: string): number => {
  return moment.duration(getFormattedDate(endDate).diff(getFormattedDate(startDate), 'days'), 'days').asDays();
};

export const DiffBtDates = (val: string): number => {
  // Input Format should be YYYY-DD-MM
  const date1 = moment(new Date(val));
  const date2 = moment(new Date(new Date().toISOString().substr(0, 10)));
  const diffDays = moment.duration(date1.diff(date2)).asDays();
  return diffDays;
};

export const getDateFormatted = (date?: DateObjectType, format?: string): string => {
  return date?.format(format ?? 'MM/DD/YYYY') ?? '';
};

export const getESTDate = () => {
  const estDateFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour12: false,
  });
  const parts = estDateFormatter.formatToParts(new Date());
  const dateParts: Partial<Intl.DateTimeFormatPartTypesRegistry> = {};
  parts.forEach(part => {
    dateParts[part.type] = part.value;
  });
  const estDate = `${dateParts.year}-${dateParts.month}-${dateParts.day}`;
  return estDate;
};

export const getMPOFormattedDate = (): { currMPODate: string; nextMPODate: string } => {
  const dateObj = {
    currMPODate: getDateFormatted(moment(new Date()), DATE_FORMAT_YEAR_MONTH_DAY).toString(),
    nextMPODate: getDateFormatted(moment(new Date()).add(1, 'days'), DATE_FORMAT_YEAR_MONTH_DAY).toString(),
  };
  return dateObj;
};

export const getCurrentDateObject = (): DateObjectType => {
  return moment();
};

export const getValueFromDate = (date?: DateObjectType, format?: string): string => {
  return date?.format(format ?? DATE_FORMAT_VALUE) ?? '';
};

export const getNextDateObject = (date: DateObjectType): DateObjectType => {
  return moment(date).add(1, 'days');
};

export const getPrevDateObject = (date: DateObjectType): DateObjectType => {
  return moment(date).add(-1, 'days');
};

export const getDateObject = (date: string | Date, format = DATE_FORMAT_YEAR_MONTH_DAY): DateObjectType => {
  return moment(date, format).utc();
};

export const getDateObj = (date: string | Date, format = DATE_FORMAT_YEAR_MONTH_DAY): DateObjectType => {
  return moment(date, format);
};

//get current date object
export const getDateObjectCurrent = (date: string, format = DATE_FORMAT_YEAR_MONTH_DAY): DateObjectType => {
  moment.locale('locale');
  return moment(date, format).utc(true);
};

export const setMomentLocale = (locale: string) => {
  try {
    let momentLocale = locale;
    switch (locale) {
      case 'de-de':
        require(`moment/locale/de`);
        momentLocale = 'de';
        break;
      case 'ja-jp':
        require(`moment/locale/ja`);
        momentLocale = 'ja';
        break;
      case 'ko-kr':
        require(`moment/locale/ko`);
        momentLocale = 'ko';
        break;
      case 'es-es':
        require(`moment/locale/es`);
        momentLocale = 'es';
        break;
      case 'it-it':
        require(`moment/locale/it`);
        momentLocale = 'it';
        break;
      case 'fr-fr':
        require(`moment/locale/fr`);
        momentLocale = 'fr';
        break;
      case 'en-gb':
        require(`moment/locale/en-gb`);
        momentLocale = 'en-gb';
        break;
      case 'pt-br':
        require(`moment/locale/pt-br`);
        momentLocale = 'pt-br';
        break;
      case 'ru-ru':
        require(`moment/locale/ru`);
        momentLocale = 'ru';
        break;
      case 'th-th':
        require(`moment/locale/th`);
        momentLocale = 'th';
        break;
      case 'vi-vn':
        require(`moment/locale/vi`);
        momentLocale = 'vi';
        break;
      case 'zh-tw':
        require(`moment/locale/zh-tw`);
        momentLocale = 'zh-tw';
        break;
      case 'ar-ae':
        require(`moment/locale/ar`);
        momentLocale = 'ar';
        break;
      case 'fi-fi':
        require(`moment/locale/fi`);
        momentLocale = 'fi';
        break;
      case 'nb-no':
        require(`moment/locale/nb`);
        momentLocale = 'nb';
        break;
      case 'pl-pl':
        require(`moment/locale/pl`);
        momentLocale = 'pl';
        break;
      case 'tr-tr':
        require(`moment/locale/tr`);
        momentLocale = 'tr';
        break;
      case 'id-id':
        require(`moment/locale/id`);
        momentLocale = 'id';
        break;
      case 'da-dk':
        require(`moment/locale/da`);
        momentLocale = 'da';
        break;
      case 'sv-se':
        require(`moment/locale/sv`);
        momentLocale = 'sv';
        break;
      case 'nl-nl':
        require(`moment/locale/nl`);
        momentLocale = 'nl';
        break;
      case 'el-gr':
        require(`moment/locale/el`);
        momentLocale = 'el';
        break;
      case 'he-il':
        require(`moment/locale/he`);
        momentLocale = 'he';
        break;
      case 'hi-in':
        require(`moment/locale/hi`);
        momentLocale = 'hi';
        break;
    }
    // Changes made to avoid the Arabic conversion of numbers
    if (locale === 'ar-ae') {
      moment.locale(momentLocale, {
        postformat: function (string: string) {
          // Replace comma with Arabic comma
          return string.replace(/,/g, '،');
        },
      });
    } else if (locale === 'hi-in') {
      moment.locale(momentLocale, {
        postformat: function (string: string) {
          // Prevent conversion to Devanagari digits by replacing them with Latin digits
          const devanagariDigits = ['०', '१', '२', '३', '४', '५', '६', '७', '८', '९'];
          return string.replace(/[०-९]/g, d => devanagariDigits.indexOf(d).toString());
        },
      });
    } else {
      moment.locale(momentLocale);
    }
  } catch (e) {
    log.warn('Failed to load moment locale package');
  }
};

// to get the dates based on the localization
export const getLocalizedDate = (
  date: DateObjectType | null | undefined,
  dateFormat?: string,
  locale?: string
): string => {
  // CNWEB-2528 Add a check to load the zh-cn locale to get correct localized date
  if (locale) {
    if (isCNLocale(locale)) {
      try {
        require(`moment/locale/zh-cn`);
        moment.locale('zh-cn');
      } catch (e) {
        log.warn('Failed to load locale - zh-cn');
      }
    } else {
      locale = locale.replace('_', '-')?.toLowerCase();
      setMomentLocale(locale);
    }
  } else {
    moment.locale('locale');
  }
  if (date) {
    //initially date will be in the english format we need to change that to the localized value
    const formattedDate = moment(date).format();
    return moment(formattedDate).format(dateFormat ?? DATE_FORMAT_DEFAULT);
  }
  return '';
};

// to get total number of days diffrence between start date and end date
export const getTotalNumberOfDays = (
  startDate: DateObjectType | undefined | null,
  endData: DateObjectType | undefined | null
): number => {
  moment.locale('locale');
  if (startDate && endData) {
    return Math.abs(moment.duration(startDate.diff(endData)).asDays() ?? 1);
  }
  return 1;
};
